import { Button, Collapse, Drawer, Empty } from "antd";
import { memo, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { releaseNotes } from "../content/release-notes";
import { GetAntIcon } from "../utils/ant_icons";

/** Previous releases listed at first, and added by each "Load more". */
const RELEASES_PAGE_SIZE = 5;

interface WhatsNewDrawerProps {
	open: boolean;
	onClose: () => void;
}

// The notes are files in the repo, so the raw HTML they hold (line breaks) is
// trusted. Never pass user or tenant content through this. Memoised so that
// Home re-renders and "Load more" don't re-parse notes already on screen.
const ReleaseNoteMarkdown = memo(function ReleaseNoteMarkdown({
	markdown,
}: {
	markdown: string;
}) {
	return (
		<div className="whats-new-markdown">
			<ReactMarkdown rehypePlugins={[rehypeRaw]}>{markdown}</ReactMarkdown>
		</div>
	);
});

/** Release notes, opened from the footer version and release notifications. */
function WhatsNewDrawer({ open, onClose }: WhatsNewDrawerProps) {
	return (
		<Drawer
			rootClassName="whats-new-drawer"
			title="What's New"
			placement="right"
			width={600}
			mask={false}
			open={open}
			onClose={onClose}
			closeIcon={GetAntIcon("rightarrow")}
			// Unmounts the content when closed, so paging and the open panel reset.
			destroyOnHidden
		>
			<WhatsNewContent />
		</Drawer>
	);
}

function WhatsNewContent() {
	const [visibleCount, setVisibleCount] = useState(RELEASES_PAGE_SIZE);
	const historyRef = useRef<HTMLElement>(null);
	// Which release header to focus once the last "Load more" removes the
	// focused button, so focus (and Esc) stays inside the drawer.
	const focusIndexRef = useRef<number | null>(null);
	const [latest, ...previous] = releaseNotes;

	useEffect(() => {
		const index = focusIndexRef.current;
		if (index === null) return;
		focusIndexRef.current = null;
		historyRef.current
			?.querySelectorAll<HTMLElement>(".ant-collapse-header")
			[index]?.focus();
	}, [visibleCount]);

	if (!latest) {
		return <Empty description="No release notes yet." />;
	}

	const visible = previous.slice(0, visibleCount);
	const hiddenCount = previous.length - visible.length;

	const loadMore = () => {
		const nextCount = visibleCount + RELEASES_PAGE_SIZE;
		if (nextCount >= previous.length) focusIndexRef.current = visibleCount;
		setVisibleCount(nextCount);
	};

	return (
		<>
			<section className="whats-new-latest">
				<div className="whats-new-latest__head">
					<span className="whats-new-latest__pulse" aria-hidden />
					<h2 className="whats-new-latest__version">v{latest.version}</h2>
					<span className="whats-new-latest__tag">Latest release</span>
				</div>
				<ReleaseNoteMarkdown markdown={latest.markdown} />
			</section>

			{previous.length > 0 && (
				<section className="whats-new-history" ref={historyRef}>
					<h2 className="whats-new-history__title">Previous releases</h2>
					<Collapse
						accordion
						bordered={false}
						className="whats-new-history__list"
						items={visible.map((note) => ({
							key: note.version,
							label: `v${note.version}`,
							children: <ReleaseNoteMarkdown markdown={note.markdown} />,
						}))}
					/>
					{hiddenCount > 0 && (
						<Button
							type="link"
							className="whats-new-history__more"
							onClick={loadMore}
						>
							Load more ({hiddenCount} older)
						</Button>
					)}
				</section>
			)}
		</>
	);
}

export default WhatsNewDrawer;
